import React, { useEffect, useState } from "react";

export default function Carousel({ images }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  const [touchDeltaX, setTouchDeltaX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [initialDistance, setInitialDistance] = useState(null);
  const [isZooming, setIsZooming] = useState(false);
  const [isPanning, setIsPanning] = useState(false);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [panStartX, setPanStartX] = useState(0);
  const [panStartY, setPanStartY] = useState(0);

  const prev = () => setActiveIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setActiveIndex((i) => (i + 1) % images.length);

  const getDistance = (touch1, touch2) => {
    return Math.hypot(touch2.clientX - touch1.clientX, touch2.clientY - touch1.clientY);
  };

  useEffect(() => {
    setActiveIndex(0);
  }, [images]);

  useEffect(() => {
    setZoomScale(1);
    setIsZooming(false);
    setInitialDistance(null);
    setIsPanning(false);
    setPanX(0);
    setPanY(0);
  }, [activeIndex, isFullscreen]);

  const handleTouchStart = (e) => {
    if (e.targetTouches.length === 2) {
      setInitialDistance(getDistance(e.targetTouches[0], e.targetTouches[1]));
      setIsZooming(true);
      return;
    }

    if (e.targetTouches.length === 1) {
      const touch = e.targetTouches[0];
      if (zoomScale > 1) {
        setIsPanning(true);
        setPanStartX(touch.clientX);
        setPanStartY(touch.clientY);
      } else {
        setTouchStartX(touch.clientX);
        setTouchDeltaX(0);
        setIsDragging(true);
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.targetTouches.length === 2 && isZooming && initialDistance) {
      const currentDistance = getDistance(e.targetTouches[0], e.targetTouches[1]);
      const nextScale = Math.min(Math.max(currentDistance / initialDistance, 1), 3);
      setZoomScale(nextScale);
      return;
    }

    if (isPanning && e.targetTouches.length === 1) {
      const touch = e.targetTouches[0];
      const deltaX = touch.clientX - panStartX;
      const deltaY = touch.clientY - panStartY;
      setPanX((prev) => prev + deltaX);
      setPanY((prev) => prev + deltaY);
      setPanStartX(touch.clientX);
      setPanStartY(touch.clientY);
      return;
    }

    if (!isDragging || e.targetTouches.length !== 1 || zoomScale > 1) return;
    const currentX = e.targetTouches[0].clientX;
    setTouchDeltaX(currentX - touchStartX);
  };

  const handleTouchEnd = () => {
    if (isZooming) {
      setIsZooming(false);
      setInitialDistance(null);
      return;
    }

    if (isPanning) {
      setIsPanning(false);
      return;
    }

    if (!isDragging) return;
    const threshold = 60;
    if (touchDeltaX < -threshold && images.length > 1) next();
    if (touchDeltaX > threshold && images.length > 1) prev();
    setTouchDeltaX(0);
    setTouchStartX(0);
    setIsDragging(false);
  };

  useEffect(() => {
    if (!isFullscreen) return;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsFullscreen(false);
      }
      if (event.key === "ArrowRight") {
        setActiveIndex((i) => (i + 1) % images.length);
      }
      if (event.key === "ArrowLeft") {
        setActiveIndex((i) => (i - 1 + images.length) % images.length);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen, images.length]);

  const trackTransform = `translateX(calc(${-activeIndex * 100}% + ${touchDeltaX}px))`;
  const trackClasses = `flex h-full ${isDragging ? "" : "transition-transform duration-300 ease-out"}`;
  const imageTransform = `scale(${zoomScale}) translate(${panX / zoomScale}px, ${panY / zoomScale}px)`;

  return (
    <div className="max-w-4xl w-full mx-auto">
      <div
        className="relative overflow-hidden rounded-xl shadow-lg bg-black min-h-[280px] md:min-h-[320px] touch-pan-y"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div className={trackClasses} style={{ transform: trackTransform }}>
          {images.map((src, index) => (
            <div key={src} className="flex-none w-full h-full overflow-hidden flex items-center justify-center relative">
              <img
                src={src}
                alt={`FAQ ${index + 1}`}
                className="w-full max-h-[70vh] object-contain"
                style={
                  index === activeIndex
                    ? { transform: imageTransform, transformOrigin: "center" }
                    : undefined
                }
                loading="lazy"
                decoding="async"
              />
            </div>
          ))}
        </div>

        <div className="absolute top-2 right-2 z-10">
          <button
            onClick={() => setIsFullscreen(true)}
            className="bg-white/50 text-black p-2 rounded-full shadow-md hover:bg-gray-200/50 focus:outline-none"
            aria-label="Open fullscreen"
          >
            ⤢
          </button>
        </div>

        {images.length > 1 && (
          <div className="absolute inset-0 flex items-center justify-between px-4">
            <button
              onClick={prev}
              className="bg-white/50 text-black p-3 rounded-full shadow-lg hover:bg-gray-200/50 focus:outline-none text-2xl"
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              onClick={next}
              className="bg-white/50 text-black p-3 rounded-full shadow-lg hover:bg-gray-200/50 focus:outline-none text-2xl"
              aria-label="Next image"
            >
              ›
            </button>
          </div>
        )}
      </div>

      <p className="text-sm text-gray-600 mt-2 text-center">
        {activeIndex + 1} / {images.length}
      </p>

      {isFullscreen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-2 touch-pan-y"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
        >
          <button
            onClick={() => setIsFullscreen(false)}
            className="absolute top-6 right-6 z-50 text-black bg-white/50 p-3 rounded-full shadow-lg hover:bg-gray-200/50 focus:outline-none"
            aria-label="Close fullscreen"
          >
            ✕
          </button>

          {images.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-6 z-50 text-black bg-white/50 p-4 rounded-full shadow-lg hover:bg-gray-200/50 focus:outline-none text-3xl"
                aria-label="Previous image"
              >
                ‹
              </button>
              <button
                onClick={next}
                className="absolute right-6 z-50 text-black bg-white/50 p-4 rounded-full shadow-lg hover:bg-gray-200/50 focus:outline-none text-3xl"
                aria-label="Next image"
              >
                ›
              </button>
            </>
          )}
          <div className="relative w-full max-w-[90vw] h-[90vh] overflow-hidden rounded-xl z-0" style={{ touchAction: "none" }}>
            <div className={trackClasses} style={{ transform: trackTransform }}>
              {images.map((src, index) => (
                <div key={src} className="flex-none w-full h-full overflow-hidden flex items-center justify-center relative">
                  <img
                    src={src}
                    alt={`FAQ ${index + 1}`}
                    className="w-full h-full object-contain"
                    style={
                      index === activeIndex
                        ? { transform: imageTransform, transformOrigin: "center" }
                        : undefined
                    }
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
